import { DEFAULT_MODELS, MODEL_SUGGESTIONS, type RuntimeProfile, type RuntimeType } from '@deskworks/shared-types';
import { useId } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** The model that applies when the agent doesn't choose one: profile default → built-in default. */
export function defaultModelFor(profile: Pick<RuntimeProfile, 'model' | 'runtimeType'> | null | undefined): string | null {
  if (!profile) return null;
  return profile.model || DEFAULT_MODELS[profile.runtimeType] || null;
}

/** Runtime types where picking a model makes sense (the local demo runtime has no model). */
export function supportsModel(type: RuntimeType | null | undefined): boolean {
  return !!type && type !== 'local';
}

/**
 * Free-text model input with suggestions for the runtime type (native datalist, so any value is allowed).
 * Empty means "use the default".
 */
export function ModelField({
  value,
  onChange,
  profile,
  label = t('Model'),
  hint,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  profile: Pick<RuntimeProfile, 'model' | 'runtimeType'> | null | undefined;
  label?: string;
  hint?: string;
  className?: string;
}) {
  const id = useId();
  const listId = `${id}-models`;
  const suggestions = profile ? (MODEL_SUGGESTIONS[profile.runtimeType] ?? []) : [];
  const fallback = defaultModelFor(profile);
  return (
    <div className={cn('grid content-start gap-1.5', className)}>
      <Label htmlFor={id}>
        {label} <span className="font-normal text-muted-foreground">{t('(optional)')}</span>
      </Label>
      <Input
        id={id}
        list={suggestions.length ? listId : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('Default: {model}', { model: fallback ?? t('provider default') })}
        spellCheck={false}
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      )}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {suggestions.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onChange(value === m ? '' : m)}
              className={cn(
                'cursor-pointer rounded-md border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
                value === m && 'border-primary bg-accent text-foreground',
              )}
            >
              {m}
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{hint ?? t('Leave empty to use the runtime’s default model.')}</p>
    </div>
  );
}
